import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppConfig } from './app-config.model';

@Injectable({
  providedIn: 'root'
})
export class RuntimeConfigService {
  private _config: AppConfig | null = null;

  constructor(private readonly http: HttpClient) {}

  async load(): Promise<void> {
    const url = `/assets/config/app-config.json?v=${Date.now()}`;

    try {
      const config = await firstValueFrom(
        this.http.get<AppConfig>(url)
      );

      this.validate(config);
      this._config = Object.freeze(config);

      // Global window bridge for dynamic endpoint factories and legacy consumers
      if (typeof window !== 'undefined') {
        (window as any).__config = {
          apiUrl: this._config.api.baseUrl,
          signalRUrl: this._config.api.signalRUrl,
          ...this._config
        };
      }
    } catch (error: any) {
      // Re-throw validation or HTTP error to prevent application startup
      throw new Error(
        `Runtime configuration loading failed: ${error?.message || error || 'Unknown error'}`
      );
    }
  }

  get config(): AppConfig {
    if (!this._config) {
      throw new Error(
        'Runtime configuration has not been initialized. Ensure RuntimeConfigService.load() was called and succeeded during startup.'
      );
    }
    return this._config;
  }

  get isLoaded(): boolean {
    return this._config !== null;
  }

  validate(config: AppConfig | null | undefined): void {
    if (!config) {
      throw new Error('Configuration payload is null or empty.');
    }

    const errors: string[] = [];

    if (!config.environment || typeof config.environment !== 'string') {
      errors.push('environment (must be a non-empty string)');
    }

    if (!config.api) {
      errors.push('api configuration object');
    } else {
      if (!config.api.baseUrl || typeof config.api.baseUrl !== 'string' || !config.api.baseUrl.startsWith('http')) {
        errors.push('api.baseUrl (must be a valid http/https URL)');
      }
      if (!config.api.signalRUrl || typeof config.api.signalRUrl !== 'string' || !config.api.signalRUrl.startsWith('http')) {
        errors.push('api.signalRUrl (must be a valid http/https URL)');
      }
    }

    if (!config.ids) {
      errors.push('ids configuration object');
    } else {
      if (config.ids.novibetAgentId === undefined || config.ids.novibetAgentId === null || isNaN(Number(config.ids.novibetAgentId))) {
        errors.push('ids.novibetAgentId (must be a valid number)');
      }
      if (config.ids.novibetLocation === undefined || config.ids.novibetLocation === null || isNaN(Number(config.ids.novibetLocation))) {
        errors.push('ids.novibetLocation (must be a valid number)');
      }
      if (!config.ids.typeId) {
        errors.push('ids.typeId (must be provided)');
      }
    }

    if (errors.length > 0) {
      throw new Error(
        `Invalid runtime configuration. Missing or invalid required fields: ${errors.join(', ')}`
      );
    }
  }
}
